import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Measure from "react-measure";

import {
	DataGrid,
	ProgressIndicator
} from "rhombus-react";

import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import { VersionsSocketListener } from "hooks/useVersionsSocket";
import { useOnMountFTMessage } from "hooks/Notification";

import { CreativeVersions } from "reducers/CreativeVersions";
import { CREATIVE_SOCIAL_VERSION_COLUMNS, CREATIVE_VERSION_COLUMNS } from "widgets/grid/columns/Versions";

import { useGridScrollUtils } from "utils/DataGrid";
import {
	MIN_GRID_HEIGHT
	, DEFAULT_ROW_HEIGHT
	, JCRM_SOCKET
} from "utils/constants";
import { mappedSelectedVersions } from "selectors/Versions";
import CustomRowRenderer from "utils/columns/LockedRow";
import { getLibraryAutoClassificationEnabled, getManualClassificationEnabled } from "selectors/Intelligence";
import { isSocialCreative } from "utils/common";

function filterVisibleColumns(props = {}) {
	const { isLibraryAutoClassificationEnabled, isManualClassificationEnabled, isSocial } = props;

	const columns = isSocial ? CREATIVE_SOCIAL_VERSION_COLUMNS : CREATIVE_VERSION_COLUMNS;

	if (isLibraryAutoClassificationEnabled && isManualClassificationEnabled) {
		return columns;
	}

	return columns.filter((column) => {
		if (column.key === "autoClassificationStatus") {
			return isLibraryAutoClassificationEnabled;
		} else if (column.key === "labelsCnt") {
			return isManualClassificationEnabled;
		}

		return true;
	});
}

export default function Grid({ sort="", libraryId, creativeId, isLoading }){
	const dispatch = useDispatch();
	const [sortColumn, sortDirection] = sort.split(":");

	const creativeVersions = useSelector(state => state.creativeVersions.items);
	const isLoaded = useSelector(state => state.creativeVersions.isLoaded);
	const adType = useSelector(state => state.creativeDetails.adType);
	const mappedVersions = useSelector(state => mappedSelectedVersions(state));
	const isLibraryAutoClassificationEnabled = useSelector(getLibraryAutoClassificationEnabled);
	const isManualClassificationEnabled = useSelector(getManualClassificationEnabled);
	const isSocial = isSocialCreative({ adType });

	// Connect to sockets for version updates after AI generation
	useOnMountFTMessage(({ detail }) => {
		let { message } = detail
			, { task, status, data } = message;

		if (message.origin !== JCRM_SOCKET) { return; }

		const creativeIdFromMessage = data.creatives[0].id;
		if (/Generate assets and versions/gi.test(task)
			&& /success/gi.test(status)
			&& creativeIdFromMessage === Number(creativeId)) {
			dispatch(CreativeVersions.loadCreativeVersions(libraryId, creativeId));
		}
	});

	const visibleColumns = useMemo(() => {
		const visibleProps = {
			isLibraryAutoClassificationEnabled,
			isManualClassificationEnabled,
			isSocial,
		};

		return filterVisibleColumns(visibleProps);
	}, [isLibraryAutoClassificationEnabled, isManualClassificationEnabled, isSocial]);

	useEffect(() => {
		if (isSocial) {
			dispatch(CreativeVersions.updateColumnFilter("status", { displayName: "Published" }));
		} else {
			dispatch(CreativeVersions.updateColumnFilter("status", { displayName: "Status" }));
		}
	}, [isSocial]);

	const dataGridRef = useGridScrollUtils([creativeVersions]);

	function getCreativeVersionRow(rowID) {
		return{ ...mappedVersions[rowID] } ;
	}

	useTooltipRebuild(creativeVersions);

	const dataGridProps = {
		columns: visibleColumns,
		rowGetter:  getCreativeVersionRow,
		rowsCount:  creativeVersions.length,
		rowRenderer: CustomRowRenderer,
		rowHeight:  DEFAULT_ROW_HEIGHT,
		sortColumn,
		sortDirection,
		onGridSort: (col, dir) => dispatch(CreativeVersions.setSort(col, dir)),
		enableCellAutoFocus: false, /*
			This is a ReactDataGrid setting that defaults to `true`, which seems to make it fight
			with anything else trying to utilize regular `tabindex` control flow, like blade forms.
		*/
	};

	return (
		<VersionsSocketListener>
			<Measure>
				{(obj) => {
					let gridHeight = MIN_GRID_HEIGHT;
					if(obj.contentRect && obj.contentRect.entry && obj.contentRect.entry.height) {
						gridHeight = obj.contentRect.entry.height;
					}

					return (
						<div className="measure-component" ref={obj.measureRef}>
							<ProgressIndicator isLoading={!isLoaded} />
							<div ref={dataGridRef}>
								<DataGrid
									{...dataGridProps}
									minHeight={gridHeight}
								/>
							</div>
						</div>
					);
				}}
			</Measure>
		</VersionsSocketListener>
	);
}

export const PRIVATE = { filterVisibleColumns };

Grid.displayName = "VersionGrid";
